// @figma file=pkCGby54zXzTsccXvHqzO6 node=tbd name="PageLayout" set="mytaible-shadcn-studio" verified=tbd
import * as React from 'react';
import { cn } from '../../lib/utils';

// ─── PageLayout ───────────────────────────────────────────────────────────────

export type PageLayoutVariant = 'single' | 'dashboard' | 'split' | 'form';

export interface PageLayoutProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * - `single`    Full-width, vertically stacked sections. Default for list / resource pages.
   * - `dashboard` Stat-cards row (PageLayoutStats) above a main body (PageLayoutBody).
   * - `split`     Wide primary area (PageLayoutBody) beside a narrow panel (PageLayoutSide).
   * - `form`      Centered, width-constrained column for settings / edit pages.
   */
  variant?: PageLayoutVariant;
  /**
   * Tailwind classes controlling outer padding and section gap.
   * Defaults to `'py-6 px-4 sm:px-6 gap-4 sm:gap-6 lg:gap-8'` — mirrors the spacing
   * previously hardcoded in shell-05's <main> tag, now owned by PageLayout as the
   * single source of truth for page-level spacing.
   * Override freely: e.g. `'py-4 px-4 sm:px-6 gap-4'` for a tighter page.
   */
  spacingClassName?: string;
}

const DEFAULT_SPACING = 'py-6 px-4 sm:px-6 gap-4 sm:gap-6 lg:gap-8';

const variantBase: Record<PageLayoutVariant, string> = {
  single: 'flex flex-col mx-auto w-full max-w-7xl',
  dashboard: 'flex flex-col mx-auto w-full max-w-7xl',
  split: 'flex flex-col lg:flex-row lg:items-start mx-auto w-full max-w-7xl',
  form: 'flex flex-col mx-auto w-full max-w-2xl',
};

const PageLayout = React.forwardRef<HTMLDivElement, PageLayoutProps>(
  ({ variant = 'single', spacingClassName = DEFAULT_SPACING, className, ...props }, ref) => (
    <div ref={ref} className={cn(variantBase[variant], spacingClassName, className)} {...props} />
  ),
);
PageLayout.displayName = 'PageLayout';

// ─── PageLayoutStats ──────────────────────────────────────────────────────────
// Responsive stat-card grid. Place inside a `dashboard` PageLayout above PageLayoutBody.

export interface PageLayoutStatsProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Number of columns on ≥sm breakpoint (default 4). */
  cols?: 2 | 3 | 4 | 5;
}

const colsClass: Record<NonNullable<PageLayoutStatsProps['cols']>, string> = {
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-3',
  4: 'grid-cols-2 sm:grid-cols-4',
  5: 'grid-cols-2 sm:grid-cols-5',
};

const PageLayoutStats = React.forwardRef<HTMLDivElement, PageLayoutStatsProps>(
  ({ cols = 4, className, ...props }, ref) => (
    <div ref={ref} className={cn('grid gap-4', colsClass[cols], className)} {...props} />
  ),
);
PageLayoutStats.displayName = 'PageLayoutStats';

// ─── PageLayoutBody ───────────────────────────────────────────────────────────
// Primary content area. Grows to fill available space in split layouts.

export type PageLayoutBodyProps = React.HTMLAttributes<HTMLDivElement>;

const PageLayoutBody = React.forwardRef<HTMLDivElement, PageLayoutBodyProps>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('min-w-0 flex-1 flex flex-col gap-6', className)} {...props} />
  ),
);
PageLayoutBody.displayName = 'PageLayoutBody';

// ─── PageLayoutSide ───────────────────────────────────────────────────────────
// Secondary sidebar panel. Use inside a `split` PageLayout beside PageLayoutBody.

export interface PageLayoutSideProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Width of the side panel (default 'md' = 20rem). */
  width?: 'sm' | 'md' | 'lg';
}

const sideWidthClass: Record<NonNullable<PageLayoutSideProps['width']>, string> = {
  sm: 'lg:w-64',
  md: 'lg:w-80',
  lg: 'lg:w-96',
};

const PageLayoutSide = React.forwardRef<HTMLDivElement, PageLayoutSideProps>(
  ({ width = 'md', className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('w-full shrink-0 flex flex-col gap-4', sideWidthClass[width], className)}
      {...props}
    />
  ),
);
PageLayoutSide.displayName = 'PageLayoutSide';

// ─── PageLayoutSection ────────────────────────────────────────────────────────
// Labeled sub-section within any layout. Renders an optional heading + content.

export interface PageLayoutSectionProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Section heading text (optional). */
  heading?: string;
  /** Smaller description rendered under the heading. */
  description?: string;
  /** Renders heading + children side-by-side on wider screens (useful for settings rows). */
  horizontal?: boolean;
}

const PageLayoutSection = React.forwardRef<HTMLDivElement, PageLayoutSectionProps>(
  ({ heading, description, horizontal = false, className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        horizontal ? 'flex test flex-col gap-4 sm:flex-row sm:gap-8' : 'flex flex-col gap-3',
        className,
      )}
      {...props}
    >
      {(heading || description) && (
        <div className={cn('flex flex-col gap-1', horizontal && 'sm:w-64 sm:shrink-0')}>
          {heading && (
            <h2 className="text-sm font-semibold text-foreground leading-snug">{heading}</h2>
          )}
          {description && (
            <p className="text-sm text-muted-foreground leading-snug">{description}</p>
          )}
        </div>
      )}
      <div className={cn('flex flex-col gap-4', horizontal && 'flex-1')}>{children}</div>
    </div>
  ),
);
PageLayoutSection.displayName = 'PageLayoutSection';

// ─── Exports ──────────────────────────────────────────────────────────────────

export { PageLayout, PageLayoutBody, PageLayoutSection, PageLayoutSide, PageLayoutStats };
